/**
 * Validation logic for the Create String Group form.
 *
 * Pure functions — no side effects, no React dependency. Returns null when
 * valid, a user-facing error string otherwise.
 */

import {
  SG_NAME_MIN_LENGTH,
  SG_NAME_MAX_LENGTH,
  SG_NAME_PATTERN,
  SG_DESCRIPTION_MAX_LENGTH,
} from '../constants';

import type { SelectProps } from '@amzn/awsui-components-console';
import type { CreateStringGroupFormData } from '../types';

/**
 * True when the name satisfies every rule the form advertises: the
 * `SG_NAME_MIN_LENGTH` product minimum, the `SG_NAME_MAX_LENGTH` maximum, and
 * the `SG_NAME_PATTERN` character set mirrored from the Smithy
 * `StringGroupIdentifier` constraint (which is why spaces are rejected).
 *
 * Shared by `getNameError` and `isFormValid` so the field error and the submit
 * button can never disagree.
 */
function isNameValid(name: string): boolean {
  const trimmed = name.trim();
  return (
    trimmed.length >= SG_NAME_MIN_LENGTH &&
    trimmed.length <= SG_NAME_MAX_LENGTH &&
    SG_NAME_PATTERN.test(trimmed)
  );
}

export function getNameError(
  name: string,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  const trimmed = name.trim();
  if (trimmed === '') return 'Name is required.';
  if (trimmed.length < SG_NAME_MIN_LENGTH)
    return `Name must be at least ${SG_NAME_MIN_LENGTH} characters.`;
  if (trimmed.length > SG_NAME_MAX_LENGTH)
    return `Name must be ${SG_NAME_MAX_LENGTH} characters or fewer.`;
  if (!SG_NAME_PATTERN.test(trimmed))
    return 'Name must use only letters, digits, hyphens, and underscores. No spaces allowed.';
  return null;
}

export function getDescriptionError(
  description: string,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  if (description.trim() === '') return 'Description is required.';
  if (description.trim().length > SG_DESCRIPTION_MAX_LENGTH)
    return `Description must be ${SG_DESCRIPTION_MAX_LENGTH} characters or fewer.`;
  return null;
}

export function getTeamNameError(
  teamName: string,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  if (teamName.trim() === '') return 'Team name is required.';
  return null;
}

export function getSourceLocaleError(
  sourceLocale: SelectProps.Option | null,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  if (!sourceLocale) return 'Source locale is required.';
  return null;
}

export function getCxFamilyError(
  cxFamilyId: string,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  if (!cxFamilyId) return 'CX family is required.';
  return null;
}

export function getCxIdsError(
  selectedCount: number,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  if (selectedCount === 0) return 'At least one CX ID is required.';
  return null;
}

/** Returns true when all required fields pass validation. */
export function isFormValid(data: CreateStringGroupFormData): boolean {
  return (
    isNameValid(data.name) &&
    data.description.trim() !== '' &&
    data.description.length <= SG_DESCRIPTION_MAX_LENGTH &&
    data.teamName.trim() !== '' &&
    data.cxFamilyId.trim() !== '' &&
    data.cxIds.length > 0 &&
    data.sourceLocale !== null
  );
}
